import React, { useCallback, useEffect, useState } from "react";
import { Copy, Eye, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import {
  cloneTemplate,
  createTemplate,
  deactivateTemplate,
  getTemplate,
  listBankCodesForTemplates,
  listTemplates,
  previewTemplate,
  replaceTemplateColumns,
  updateTemplate,
} from "./bankTemplateApi";
import { bankApiError } from "./bankErrorUtils";

const BIND_KEYS = [
  "employee_id",
  "account_number",
  "ifsc",
  "amount",
  "account_holder_name",
  "payment_ref",
  "narration",
  "purpose",
  "beneficiary_code",
  "beneficiary_bank",
  "address_1",
  "address_2",
  "city",
  "state",
  "literal",
  "config",
  "sequence",
];

const FORMAT_HINTS = ["", "amount_2dp", "amount_int", "uppercase", "lowercase", "trim"];

const FILE_TYPES = ["CSV", "TXT", "XLSX", "DAT", "PIPE", "TAB", "FIXED"];

const emptyTemplate = () => ({
  bank_code: "AXIS",
  bank_name: "",
  name: "",
  version: "1",
  country: "IN",
  file_type: "CSV",
  delimiter: "",
  encoding: "utf-8",
  line_terminator: "\n",
  file_extension: "csv",
  header_template: "",
  footer_template: "",
  narration_rule: {},
  validation_rule: {},
  is_active: true,
  columns: [],
});

const emptyColumn = (sequence) => ({
  sequence,
  name: "",
  bind_key: "employee_id",
  literal_value: "",
  width_bytes: null,
  pad_char: "",
  align: "left",
  truncate: true,
  is_required: false,
  format_hint: "",
  default_value: "",
  validation_rule: {},
});

export default function BankTemplateAdmin() {
  const [templates, setTemplates] = useState([]);
  const [bankCodes, setBankCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [editing, setEditing] = useState(null); // template being edited
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [tpls, codes] = await Promise.all([
        listTemplates(),
        listBankCodesForTemplates(),
      ]);
      setTemplates(tpls);
      setBankCodes(codes);
    } catch (e) {
      setError(bankApiError(e) ||"Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => setEditing(emptyTemplate());

  const startEdit = async (templateId) => {
    setSaving(true);
    try {
      const tpl = await getTemplate(templateId);
      setEditing({
        ...tpl,
        // Columns come with column_id / template_id — keep them; UI only cares about
        // the editable fields.
      });
    } catch (e) {
      setError(bankApiError(e) ||"Failed to load template");
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!editing.name || !editing.bank_code) {
      setError("Name and bank code are required");
      return;
    }
    setSaving(true);
    setError("");
    setInfo("");
    try {
      let templateId;
      if (editing.format_id) {
        await updateTemplate(editing.format_id, {
          name: editing.name,
          version: editing.version,
          file_type: editing.file_type,
          delimiter: editing.delimiter || null,
          encoding: editing.encoding,
          line_terminator: editing.line_terminator,
          file_extension: editing.file_extension,
          header_template: editing.header_template || null,
          footer_template: editing.footer_template || null,
          narration_rule: editing.narration_rule,
          validation_rule: editing.validation_rule,
          is_active: editing.is_active,
        });
        templateId = editing.format_id;
      } else {
        const created = await createTemplate({
          bank_code: editing.bank_code,
          bank_name: editing.bank_name,
          name: editing.name,
          version: editing.version,
          country: editing.country,
          file_type: editing.file_type,
          delimiter: editing.delimiter || null,
          encoding: editing.encoding,
          line_terminator: editing.line_terminator,
          file_extension: editing.file_extension,
          header_template: editing.header_template || null,
          footer_template: editing.footer_template || null,
          narration_rule: editing.narration_rule,
          validation_rule: editing.validation_rule,
          is_active: editing.is_active,
          columns: editing.columns.map(({ column_id, template_id, ...col }) => col),
        });
        templateId = created.format_id;
      }
      if (editing.format_id) {
        await replaceTemplateColumns(
          templateId,
          editing.columns.map(({ column_id, template_id, ...col }) => col)
        );
      }
      setInfo("Template saved.");
      setEditing(null);
      await load();
    } catch (e) {
      setError(bankApiError(e) ||"Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async (templateId) => {
    setSaving(true);
    setError("");
    try {
      const data = await previewTemplate(templateId, { rowCount: 3 });
      setPreview({ templateId, ...data });
    } catch (e) {
      setError(bankApiError(e) ||"Failed to generate preview");
    } finally {
      setSaving(false);
    }
  };

  const doClone = async (templateId) => {
    setSaving(true);
    try {
      await cloneTemplate(templateId);
      setInfo("Template cloned (inactive).");
      await load();
    } catch (e) {
      setError(bankApiError(e) ||"Failed to clone template");
    } finally {
      setSaving(false);
    }
  };

  const doDeactivate = async (templateId) => {
    if (!window.confirm("Deactivate this template? It won't be used for new bank files."))
      return;
    setSaving(true);
    try {
      await deactivateTemplate(templateId);
      await load();
    } catch (e) {
      setError(bankApiError(e) ||"Failed to deactivate template");
    } finally {
      setSaving(false);
    }
  };

  const addColumn = () => {
    setEditing((e) => ({
      ...e,
      columns: [...e.columns, emptyColumn(e.columns.length + 1)],
    }));
  };

  const updateColumn = (idx, patch) => {
    setEditing((e) => ({
      ...e,
      columns: e.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const removeColumn = (idx) => {
    setEditing((e) => ({
      ...e,
      columns: e.columns
        .filter((_, i) => i !== idx)
        .map((c, i) => ({ ...c, sequence: i + 1 })),
    }));
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Bank Templates</h2>
        <button
          type="button"
          onClick={startNew}
          style={{
            marginLeft: "auto",
            padding: "6px 12px",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Plus size={14} /> New template
        </button>
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}
      {info && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#dcfce7",
            color: "#166534",
            borderRadius: 6,
          }}
        >
          {info}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {loading && <div>Loading…</div>}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={{ padding: 8 }}>Bank</th>
              <th style={{ padding: 8 }}>Name</th>
              <th style={{ padding: 8 }}>File type</th>
              <th style={{ padding: 8 }}>Version</th>
              <th style={{ padding: 8 }}>Columns</th>
              <th style={{ padding: 8 }}>Active</th>
              <th style={{ padding: 8 }}></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.format_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{t.bank_code}</td>
                <td style={{ padding: 8 }}>{t.name}</td>
                <td style={{ padding: 8 }}>{t.file_type}</td>
                <td style={{ padding: 8 }}>{t.version}</td>
                <td style={{ padding: 8 }}>{t.columns?.length ?? 0}</td>
                <td style={{ padding: 8 }}>{t.is_active ? "yes" : "no"}</td>
                <td style={{ padding: 8, display: "flex", gap: 6 }}>
                  <button
                    onClick={() => doPreview(t.format_id)}
                    title="Preview"
                    style={{ padding: 4 }}
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => startEdit(t.format_id)}
                    title="Edit"
                    style={{ padding: 4 }}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => doClone(t.format_id)}
                    title="Clone"
                    style={{ padding: 4 }}
                  >
                    <Copy size={14} />
                  </button>
                  {t.is_active && (
                    <button
                      onClick={() => doDeactivate(t.format_id)}
                      title="Deactivate"
                      style={{ padding: 4, color: "#991b1b" }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {templates.length === 0 && !loading && (
              <tr>
                <td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                  No templates yet. Create one to override the code plugin defaults.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <TemplateEditor
          editing={editing}
          setEditing={setEditing}
          bankCodes={bankCodes}
          addColumn={addColumn}
          updateColumn={updateColumn}
          removeColumn={removeColumn}
          onSave={saveTemplate}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}

      {preview && (
        <PreviewModal preview={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

function TemplateEditor({
  editing,
  setEditing,
  bankCodes,
  addColumn,
  updateColumn,
  removeColumn,
  onSave,
  onClose,
  saving,
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 40,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          width: "90%",
          maxWidth: 1000,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>
            {editing.format_id ? "Edit template" : "New template"}
          </h3>
          <button onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr", marginTop: 12 }}>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Bank code</div>
            <select
              value={editing.bank_code}
              disabled={Boolean(editing.format_id)}
              onChange={(e) => setEditing({ ...editing, bank_code: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            >
              {bankCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Name</div>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Version</div>
            <input
              value={editing.version}
              onChange={(e) => setEditing({ ...editing, version: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>File type</div>
            <select
              value={editing.file_type}
              onChange={(e) => setEditing({ ...editing, file_type: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            >
              {FILE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Delimiter</div>
            <input
              value={editing.delimiter}
              onChange={(e) => setEditing({ ...editing, delimiter: e.target.value })}
              placeholder=", | \t"
              style={{ width: "100%", padding: 6, fontFamily: "monospace" }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>File extension</div>
            <input
              value={editing.file_extension}
              onChange={(e) => setEditing({ ...editing, file_extension: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Encoding</div>
            <input
              value={editing.encoding}
              onChange={(e) => setEditing({ ...editing, encoding: e.target.value })}
              style={{ width: "100%", padding: 6 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "#64748b" }}>Line terminator</div>
            <input
              value={editing.line_terminator}
              onChange={(e) => setEditing({ ...editing, line_terminator: e.target.value })}
              placeholder="\n or \r\n"
              style={{ width: "100%", padding: 6, fontFamily: "monospace" }}
            />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#64748b" }}>Header template (optional)</div>
          <textarea
            value={editing.header_template || ""}
            onChange={(e) => setEditing({ ...editing, header_template: e.target.value })}
            rows={2}
            style={{ width: "100%", padding: 6, fontFamily: "monospace" }}
          />
        </label>
        <label style={{ display: "block", marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "#64748b" }}>Footer template (optional)</div>
          <textarea
            value={editing.footer_template || ""}
            onChange={(e) => setEditing({ ...editing, footer_template: e.target.value })}
            rows={2}
            style={{ width: "100%", padding: 6, fontFamily: "monospace" }}
          />
        </label>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h4 style={{ margin: 0 }}>Columns</h4>
            <button onClick={addColumn} style={{ padding: "4px 8px", marginLeft: "auto" }}>
              <Plus size={12} /> Add column
            </button>
          </div>
          <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 12 }}>
                <th style={{ padding: 4 }}>Seq</th>
                <th style={{ padding: 4 }}>Name</th>
                <th style={{ padding: 4 }}>Bind</th>
                <th style={{ padding: 4 }}>Literal / config key</th>
                <th style={{ padding: 4 }}>Width</th>
                <th style={{ padding: 4 }}>Align</th>
                <th style={{ padding: 4 }}>Pad</th>
                <th style={{ padding: 4 }}>Hint</th>
                <th style={{ padding: 4 }}></th>
              </tr>
            </thead>
            <tbody>
              {editing.columns.map((col, idx) => (
                <tr key={idx} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: 4 }}>{col.sequence}</td>
                  <td style={{ padding: 4 }}>
                    <input
                      value={col.name}
                      onChange={(e) => updateColumn(idx, { name: e.target.value })}
                      style={{ width: 140, padding: 3 }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <select
                      value={col.bind_key}
                      onChange={(e) => updateColumn(idx, { bind_key: e.target.value })}
                      style={{ padding: 3 }}
                    >
                      {BIND_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      value={col.literal_value || ""}
                      onChange={(e) => updateColumn(idx, { literal_value: e.target.value })}
                      placeholder={col.bind_key === "literal" ? "value" : ""}
                      style={{ width: 120, padding: 3 }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      type="number"
                      value={col.width_bytes || ""}
                      onChange={(e) =>
                        updateColumn(idx, {
                          width_bytes: e.target.value
                            ? parseInt(e.target.value, 10)
                            : null,
                        })
                      }
                      style={{ width: 60, padding: 3 }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <select
                      value={col.align || "left"}
                      onChange={(e) => updateColumn(idx, { align: e.target.value })}
                      style={{ padding: 3 }}
                    >
                      <option value="left">left</option>
                      <option value="right">right</option>
                    </select>
                  </td>
                  <td style={{ padding: 4 }}>
                    <input
                      value={col.pad_char || ""}
                      maxLength={1}
                      onChange={(e) => updateColumn(idx, { pad_char: e.target.value })}
                      style={{ width: 30, padding: 3, textAlign: "center" }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <select
                      value={col.format_hint || ""}
                      onChange={(e) => updateColumn(idx, { format_hint: e.target.value })}
                      style={{ padding: 3 }}
                    >
                      {FORMAT_HINTS.map((h) => (
                        <option key={h} value={h}>
                          {h || "—"}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 4 }}>
                    <button onClick={() => removeColumn(idx)} style={{ padding: 3 }}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {editing.columns.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 10, textAlign: "center", color: "#64748b" }}>
                    No columns yet. Add columns to define the file layout.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 12px" }}>
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              padding: "8px 12px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 40,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          width: "90%",
          maxWidth: 900,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>Preview</h3>
          <button onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
          Rows shown are fixture data — not real employees. Extension:{" "}
          <code>{preview.file_extension}</code>, encoding: <code>{preview.encoding}</code>
          {preview.is_binary
            ? `, binary size: ${preview.binary_size_bytes} bytes`
            : ""}
        </div>
        {!preview.is_binary && (
          <pre
            style={{
              marginTop: 12,
              padding: 12,
              background: "#0f172a",
              color: "#e2e8f0",
              borderRadius: 6,
              fontSize: 12,
              overflowX: "auto",
              maxHeight: 320,
            }}
          >
            {preview.header}
            {"\n"}
            {preview.lines.join("\n")}
          </pre>
        )}
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <strong>Validation report:</strong>
          <pre style={{ background: "#f8fafc", padding: 8, borderRadius: 4 }}>
            {JSON.stringify(preview.validation_report, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}
